'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { formatDate } from '@/features/dates/date-helpers';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { generateDatesFromRange } from '../bulk-form-utils';
import type { BulkFormValues } from '../zod/bulk-form-schema';

// Status options mapping (matches RecReviewStatus enum)
const STATUS_OPTIONS_FORM = [
	{ value: 'N', label: 'No Change' },
	{ value: 'R', label: 'Review' },
	{ value: 'P', label: 'Pending' },
	{ value: 'S', label: 'Success' },
	{ value: 'E', label: 'Error' },
];

interface BulkConfirmationDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	pendingData: BulkFormValues | null;
	onConfirm: (allowOverwrite?: boolean) => void;
	isPending: boolean;
	conflicts?: Array<{
		date: string;
		existingRate?: number | undefined;
		message: string;
	}> | null;
}

export function BulkConfirmationDialog({
	open,
	onOpenChange,
	pendingData,
	onConfirm,
	isPending,
	conflicts,
}: BulkConfirmationDialogProps) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='tw-max-w-2xl'>
				<DialogHeader>
					<DialogTitle>Confirm Bulk Creation</DialogTitle>
					<DialogDescription>
						Review the recommendations that will be created:
					</DialogDescription>
				</DialogHeader>

				{pendingData && (
					<div className='tw-space-y-4'>
						<div className='tw-grid tw-grid-cols-2 tw-gap-4 tw-p-4 tw-bg-muted tw-rounded-lg'>
							<div>
								<strong>Date Range:</strong>
								<br />
								{format(pendingData.startDate, 'MM/dd/yyyy')} to{' '}
								{format(pendingData.endDate, 'MM/dd/yyyy')}
							</div>
							<div>
								<strong>Rate:</strong> ${pendingData.rate}
								<br />
								<strong>Status:</strong>{' '}
								{STATUS_OPTIONS_FORM.find((s) => s.value === pendingData.status)
									?.label || 'Open'}
							</div>
						</div>

						<div>
							<strong>Selected Days:</strong>
							<div className='tw-flex tw-flex-wrap tw-gap-1 tw-mt-2'>
								{Object.entries(pendingData.daysOfWeek)
									.filter(([_, selected]) => selected)
									.map(([day, _]) => (
										<Badge key={day} variant='secondary'>
											{day.charAt(0).toUpperCase() + day.slice(1, 3)}
										</Badge>
									))}
							</div>
						</div>

						{conflicts && conflicts.length > 0 ? (
							<div className='tw-p-4 tw-bg-orange-50 dark:tw-bg-orange-950/20 tw-rounded-lg tw-border tw-border-orange-200 dark:tw-border-orange-800'>
								<div className='tw-flex tw-items-center tw-gap-2 tw-mb-3'>
									<div className='tw-w-4 tw-h-4 tw-bg-orange-500 tw-rounded-full tw-flex-shrink-0' />
									<strong className='tw-text-foreground'>
										Existing Recommendations Found
									</strong>
								</div>
								<p className='tw-text-sm tw-text-muted-foreground tw-mb-3'>
									The following dates already have rate recommendations:
								</p>
								<div className='tw-max-h-32 tw-overflow-y-auto tw-space-y-1'>
									{conflicts.map((conflict, index) => (
										<div
											key={index}
											className='tw-text-xs tw-bg-background tw-p-2 tw-rounded tw-border'
										>
											<span className='tw-font-medium'>
												{formatDate(conflict.date, 'MM/DD/YYYY')}
											</span>
											{conflict.existingRate && (
												<span className='tw-text-muted-foreground tw-ml-2'>
													(Current: ${conflict.existingRate})
												</span>
											)}
										</div>
									))}
								</div>
								<p className='tw-text-sm tw-text-muted-foreground tw-mt-3'>
									Click "Overwrite Existing" to replace them, or "Cancel" to go
									back and modify your selection.
								</p>
							</div>
						) : (
							<div className='tw-p-3 tw-bg-blue-50 dark:tw-bg-blue-950/20 tw-rounded-lg'>
								<p className='tw-text-sm'>
									<strong>
										{
											generateDatesFromRange(
												pendingData.startDate,
												pendingData.endDate,
												pendingData.daysOfWeek,
											).length
										}
									</strong>{' '}
									recommendations will be created.
								</p>
								{pendingData.notes && (
									<p className='tw-text-xs tw-text-muted-foreground tw-mt-1'>
										Notes: {pendingData.notes}
									</p>
								)}
							</div>
						)}
					</div>
				)}

				<DialogFooter>
					<Button variant='outline' onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					{conflicts && conflicts.length > 0 ? (
						<Button
							onClick={() => onConfirm(true)}
							disabled={isPending}
							variant='destructive'
						>
							{isPending ? (
								<>
									<Loader2 className='tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin' />
									Overwriting...
								</>
							) : (
								'Overwrite Existing'
							)}
						</Button>
					) : (
						<Button onClick={() => onConfirm(false)} disabled={isPending}>
							{isPending ? (
								<>
									<Loader2 className='tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin' />
									Creating...
								</>
							) : (
								'Create Recommendations'
							)}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
